import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRangeAllowZero, validateRange } from '../../range-validate';
import {
  SsyCalculatorService, SsyResult, SSY_CONSTRAINTS, SSY_DEPOSIT_YEARS, SSY_MATURITY_YEARS
} from './ssy-calculator.service';

@Component({
  selector: 'app-ssy-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './ssy-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ssy-calculator.scss']
})
export class SsyCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(SsyCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = SSY_CONSTRAINTS;
  readonly Math = Math;
  readonly depositYears  = SSY_DEPOSIT_YEARS;
  readonly maturityYears = SSY_MATURITY_YEARS;

  girlAge = 5;
  yearlyDeposit = 50_000;
  annualRate = 8.2;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     SsyResult | null = null;

  get maturityAge(): number { return this.girlAge + this.maturityYears; }

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e0 = validateRangeAllowZero(this.girlAge, this.C.girlAge.min, this.C.girlAge.max, 'Girl child age');
    if (e0) errors['girlAge'] = e0;
    const e1 = validateRange(this.yearlyDeposit, this.C.yearlyDeposit.min, this.C.yearlyDeposit.max, 'Yearly deposit');
    if (e1) errors['yearlyDeposit'] = e1;
    const e2 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Interest rate');
    if (e2) errors['annualRate'] = e2;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'girlAge') this.girlAge = Math.floor(this.girlAge || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { girlAge: true, yearlyDeposit: true, annualRate: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.yearlyDeposit, this.annualRate);
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Invested', value: r.totalInvested, color: '#3b82f6' },
        { label: 'Interest Earned', value: r.totalInterest, color: '#10b981' }
      ], 'Maturity Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.rows.map(row => `Yr ${row.year}`),
        [{ label: 'Balance', data: r.rows.map(row => Math.round(row.closingBalance)), color: '#3b82f6' }]
      );
    }, 60);
  }

  reset(): void {
    this.girlAge         = 5;
    this.yearlyDeposit    = 50_000;
    this.annualRate       = 8.2;
    this.formErrors       = {};
    this.touched          = {};
    this.showFormErrors   = false;
    this.calculated       = false;
    this.result           = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
